import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { H3, MenuItem, Select } from 'design-system';
import {
  CategoryField,
  CategoryChoiceValue,
  CategoryChoiceLongHint,
} from '@scale/shared/types/params/textcollection';
import React from 'react';
import {
  CategoryChooseMultiple,
  useCategorizationFieldSectionStyles,
} from 'react-scale/components/scalers/tasks/textcollection/CategoryFieldSection';
import { getAcceptableCustomerAuditGroupLabels } from 'react-scale/components/scalers/tasks/textcollection/FieldPreview';
import StatusLight, {
  Lights,
} from 'react-scale/dashboard.scale.com/components/Tasks/TaskRow/StatusLight';
import { useToggle } from 'react-use-v13';
import { TextCollectionConfig } from '@scale/shared/defaults';
import { TextCollectionAnnotation } from '@scale/shared/types/response/textcollection';

export enum MultipleCategoryAuditStatus {
  Correct = 'Correct',
  Acceptable = 'Acceptable',
  Incorrect = 'Incorrect',
  Missing = 'Missing',
}

enum CustomerAuditResultStatus {
  // if Correct, do not concatenate anything
  Acceptable = 'customer-audit-result-status-acceptable',
  Incorrect = 'customer-audit-result-status-incorrect',
  Missing = 'customer-audit-result-status-missing',
}

/*
  Each value in the response is of the form `STATUS,...CHOICE`
  where status is one of CustomerAuditResultStatus, and choice is 
  a normal CategoryChoiceValue selection of the available options.

  The only exception is for Correct values. They have the default form
  i.e `CHOICE`.

  So when split across delimiter it will result in:
  choice.split(',') = [previousStatus, ...choiceWithoutStatus]
  Except in Correct cases, it will result in:
  choice.split(',') = [choice]
*/

const getUpdatedCustomerAuditStatusResponse = (
  choice: string,
  status: MultipleCategoryAuditStatus,
) => {
  if (status === MultipleCategoryAuditStatus.Correct) {
    return choice;
  } else {
    return [CustomerAuditResultStatus[status], choice].join(',');
  }
};

export const CategoryChooseMultipleAcceptableAudit: React.FC<{
  field: CategoryField;
  response: CategoryChoiceValue[];
  updateResponse: (answer: CategoryChoiceValue[]) => void;
  suggestionsSet: Set<CategoryChoiceValue>;
  suggestionsHint?: string;
  disabled?: boolean;
  fieldLongHints?: Record<string, CategoryChoiceLongHint[]>;
  assignmentId?: string;
  loggedUserId?: string;
  useSemanticSearch?: boolean;
  wordEmbeddings?: Record<string, number[]>;
  embeddingDim?: number;
  textCollectionConfig?: TextCollectionConfig;
  currentAnnotations: Record<string, TextCollectionAnnotation>;
}> = ({
  field,
  response,
  updateResponse,
  suggestionsSet,
  suggestionsHint,
  disabled,
  fieldLongHints,
  assignmentId,
  loggedUserId,
  useSemanticSearch,
  wordEmbeddings,
  embeddingDim,
  textCollectionConfig,
  currentAnnotations,
}) => {
  const loadAuditStatusMap = () => {
    const statusMap: Record<string, MultipleCategoryAuditStatus> = {};
    (response as string[]).forEach(choice => {
      const [status, ...answerSplit] = choice.split(',');
      const answer = answerSplit.join(',');

      switch (status) {
        case CustomerAuditResultStatus.Acceptable: {
          statusMap[answer] = MultipleCategoryAuditStatus.Acceptable;
          break;
        }
        case CustomerAuditResultStatus.Incorrect: {
          statusMap[answer] = MultipleCategoryAuditStatus.Incorrect;
          break;
        }
        case CustomerAuditResultStatus.Missing:
          break;
        default: {
          statusMap[choice] = MultipleCategoryAuditStatus.Correct;
        }
      }
    });
    return statusMap;
  };

  const loadMissingResponse = () => {
    const missingChoices: CategoryChoiceValue[] = [];
    (response as string[]).forEach(choice => {
      const [status, ...answerSplit] = choice.split(',');
      const answer = answerSplit.join(',');
      if (status === CustomerAuditResultStatus.Missing) {
        missingChoices.push(answer);
      }
    });
    return missingChoices;
  };

  const [auditStatusMap, setAuditStatusMap] =
    React.useState<Record<string, MultipleCategoryAuditStatus>>(loadAuditStatusMap);
  const [missingResponse, setMissingResponse] =
    React.useState<CategoryChoiceValue[]>(loadMissingResponse);

  const updateSelectedAuditStatusMap = (
    selectedResponse: CategoryChoiceValue[],
    status: MultipleCategoryAuditStatus,
  ): Record<string, MultipleCategoryAuditStatus> => {
    const statusMap: Record<string, MultipleCategoryAuditStatus> = {};
    Object.keys(auditStatusMap).forEach(choice => {
      if (selectedResponse.includes(choice)) {
        statusMap[choice] = status;
      } else {
        statusMap[choice] = auditStatusMap[choice];
      }
    });
    return statusMap;
  };

  const anchorRef = React.useRef(null);

  const [hidingCorrect, toggleHidingCorrect] = useToggle(false);
  const [hidingAcceptable, toggleHidingAcceptable] = useToggle(false);
  const [hidingIncorrect, toggleHidingIncorrect] = useToggle(false);
  const [hidingMissing, toggleHidingMissing] = useToggle(false);

  const [selectedResponse, setSelectedResponse] = React.useState<CategoryChoiceValue[]>([]);

  // Reset when 'Make Changes' mode is exited
  const [responseWhenDisabled, setResponseWhenDisabled] = React.useState(response);
  /*
    Wait until response has been updated one 'Make Changes' mode is canceled

    The local storage of response is deleted upon cancel.
    We must wait until after deletion to reset.
  */
  React.useEffect(() => {
    setResponseWhenDisabled(prevResponse => {
      return response !== prevResponse && disabled === true ? response : prevResponse;
    });
  }, [response, disabled]);
  React.useEffect(() => {
    setAuditStatusMap(loadAuditStatusMap());
    setMissingResponse(loadMissingResponse());
    toggleHidingCorrect(false);
    toggleHidingIncorrect(false);
    toggleHidingIncorrect(false);
    toggleHidingMissing(false);
    setSelectedResponse([]);
  }, [responseWhenDisabled]);

  // update response
  React.useEffect(() => {
    const newResponse: CategoryChoiceValue[] = [];
    Object.keys(auditStatusMap).forEach(choice => {
      newResponse.push(getUpdatedCustomerAuditStatusResponse(choice, auditStatusMap[choice]));
    });
    missingResponse.forEach(choice => {
      newResponse.push(
        getUpdatedCustomerAuditStatusResponse(
          choice as string,
          MultipleCategoryAuditStatus.Missing,
        ),
      );
    });
    updateResponse(newResponse);
  }, [auditStatusMap, missingResponse]);

  // Clean list of response without audit status prefixes
  const [disabledResponse, setDisabledResponse] = React.useState(Object.keys(auditStatusMap));
  React.useEffect(() => {
    setDisabledResponse(Object.keys(auditStatusMap));
  }, [auditStatusMap]);

  const classes = useCategorizationFieldSectionStyles();

  const moveSelected = (status: MultipleCategoryAuditStatus) => {
    setAuditStatusMap(updateSelectedAuditStatusMap(selectedResponse, status));
    setSelectedResponse([]);
  };

  const getStatusSection = (
    status: MultipleCategoryAuditStatus,
    color: Lights,
    hiding: boolean,
    toggleHiding: React.MouseEventHandler<HTMLDivElement>,
    sectionChoices?: CategoryChoiceValue[],
  ) => {
    const sectionResponse =
      sectionChoices ??
      Object.keys(auditStatusMap).filter(choice => auditStatusMap[choice] === status);

    return (
      <React.Fragment>
        <div className={classes.acceptableAuditStatusRow} key={status} onClick={toggleHiding}>
          <div className={classes.acceptableAuditStatusContainer}>
            <div className={classes.acceptableAuditStatusItem}>
              <StatusLight color={color} />
            </div>
            <div className={classes.acceptableAuditStatusItem}>
              {status} ({sectionResponse.length})
            </div>
          </div>
          <FontAwesomeIcon
            className={classes.clickable}
            icon={hiding ? 'caret-left' : 'caret-down'}
          />
        </div>
        {!hiding && !sectionChoices && (
          <div className={classes.preview}>
            {getAcceptableCustomerAuditGroupLabels(
              sectionResponse,
              field,
              status,
              auditStatusMap,
              selectedResponse,
              setSelectedResponse,
              disabled,
            )}
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className={classes.choicesContainer} ref={anchorRef}>
      {!disabled && (
        <div className={classes.acceptableAuditStatusRow}>
          <Select
            label={`Move selected (${selectedResponse.length}) to...`}
            onChange={(e: any) => moveSelected(e.target.value)}
          >
            <div className={classes.acceptableAuditStatusContainer}>
              <div className={classes.acceptableAuditStatusMenuTitle}>
                <H3>{`Move selected (${selectedResponse.length}) to...`}</H3>
              </div>
            </div>

            {[
              [MultipleCategoryAuditStatus.Correct, Lights.Green],
              [MultipleCategoryAuditStatus.Acceptable, Lights.Yellow],
              [MultipleCategoryAuditStatus.Incorrect, Lights.Red],
            ].map(([status, color]) => {
              return (
                <MenuItem key={status} value={status}>
                  <div key={status} className={classes.acceptableAuditStatusContainer}>
                    <StatusLight key={status} color={color as Lights} />
                    <div key={status} className={classes.acceptableAuditStatusItem}>
                      {status}
                    </div>
                  </div>
                </MenuItem>
              );
            })}
          </Select>
        </div>
      )}
      {getStatusSection(
        MultipleCategoryAuditStatus.Correct,
        Lights.Green,
        hidingCorrect,
        toggleHidingCorrect,
      )}
      {getStatusSection(
        MultipleCategoryAuditStatus.Acceptable,
        Lights.Yellow,
        hidingAcceptable,
        toggleHidingAcceptable,
      )}
      {getStatusSection(
        MultipleCategoryAuditStatus.Incorrect,
        Lights.Red,
        hidingIncorrect,
        toggleHidingIncorrect,
      )}
      {getStatusSection(
        MultipleCategoryAuditStatus.Missing,
        Lights.Grey,
        hidingMissing,
        toggleHidingMissing,
        missingResponse,
      )}
      {!hidingMissing && (!disabled || missingResponse.length > 0) && (
        <CategoryChooseMultiple
          response={missingResponse}
          field={field}
          updateResponse={setMissingResponse}
          suggestionsSet={suggestionsSet}
          suggestionsHint={suggestionsHint}
          fieldLongHints={fieldLongHints}
          disabled={disabled}
          assignmentId={assignmentId}
          loggedUserId={loggedUserId}
          useSemanticSearch={useSemanticSearch}
          wordEmbeddings={wordEmbeddings}
          embeddingDim={embeddingDim}
          disabledResponse={disabledResponse}
          textCollectionConfig={textCollectionConfig}
          currentAnnotations={currentAnnotations}
        />
      )}
    </div>
  );
};
